import { useLayoutEffect, useRef, useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { PlanningBatch, PlanningResource } from '../../api/types';
import type { FeasibilityLevel } from '../fournees/feasibility';
import { batchGeometry, dayBands, makeScale, timeTicks, xOf } from './geometry';
import { cycleColor } from './colors';

/** Style de faisabilité appliqué à un bloc quand un OF est sélectionné. */
export interface BatchFeasibilityHint {
  level: FeasibilityLevel;
  icons: string[];
}

/**
 * Planning proportionnel au temps : une ligne par moyen, les fournees
 * positionnees a l'echelle. Meme composant pour la TV (lecture seule) et le
 * manager (interactif). L'echelle px/minute est identique partout ; seul
 * l'horizon visible change.
 */
export interface TimelineProps {
  resources: PlanningResource[];
  batches: PlanningBatch[];
  from: Date;
  to: Date;
  now?: Date;
  rowHeight?: number;
  onBatchClick?: (batch: PlanningBatch) => void;
  /** Placement a une position temporelle (clic sur une piste libre) — mode manager. */
  onEmptyClick?: (resourceId: number, at: Date) => void;
  selectedBatchId?: number | null;
  /** Rend les fournees droppables (dépôt d'OF) — nécessite un DndContext parent. */
  droppableBatches?: boolean;
  /** Style de faisabilité par fournée (quand un OF est sélectionné/glissé). */
  feasibilityByBatch?: Record<number, BatchFeasibilityHint>;
  /** Mode affichage d'atelier : typo agrandie, moyens = nom seul. */
  tvMode?: boolean;
  /** Badge (numéro) par fournée, pour lier un bloc à sa carte de détail. */
  badges?: Record<number, string>;
}

const RESOURCE_COL = 150;

export default function Timeline({
  resources,
  batches,
  from,
  to,
  now,
  rowHeight = 64,
  onBatchClick,
  onEmptyClick,
  selectedBatchId,
  droppableBatches = false,
  feasibilityByBatch,
  tvMode = false,
  badges,
}: TimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  // L'echelle depend de la largeur reelle de la piste (responsive).
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => setTrackWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = makeScale(from, to, trackWidth);
  const ticks = trackWidth > 0 ? timeTicks(scale) : [];
  const bands = trackWidth > 0 ? dayBands(scale) : [];
  const nowX = now && now >= from && now <= to ? xOf(scale, now) : null;

  const batchesByResource = new Map<number, PlanningBatch[]>();
  for (const b of batches) {
    const list = batchesByResource.get(b.resourceId) ?? [];
    list.push(b);
    batchesByResource.set(b.resourceId, list);
  }

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow overflow-hidden select-none">
      {/* En-tete : colonne moyen + axe temporel */}
      <div className="flex border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className={`shrink-0 px-3 py-2 font-medium text-gray-500 dark:text-gray-400 uppercase ${tvMode ? 'text-sm' : 'text-xs'}`} style={{ width: RESOURCE_COL }}>
          Moyen
        </div>
        <div ref={trackRef} className="relative flex-grow h-9">
          {ticks.map((t, i) => (
            <div key={i} className="absolute top-0 bottom-0 flex items-start" style={{ left: t.x }}>
              <div className={`h-full border-l ${t.major ? 'border-gray-400 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'}`} />
              <span className={`ml-1 mt-1 ${tvMode ? 'text-xs' : 'text-[10px]'} ${t.major ? 'font-semibold text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lignes de moyens */}
      {resources.map((r) => {
        const unavailable = r.status !== 'AVAILABLE';
        return (
          <div key={r.id} className="flex border-b dark:border-gray-800 last:border-b-0" style={{ height: rowHeight }}>
            <div className={`shrink-0 px-3 py-2 border-r dark:border-gray-700 flex flex-col justify-center ${unavailable ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`} style={{ width: RESOURCE_COL }}>
              <div className="flex items-center gap-2">
                <span className={`shrink-0 rounded-full ${tvMode ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5'} ${r.status === 'AVAILABLE' ? 'bg-green-500' : r.status === 'MAINTENANCE' ? 'bg-amber-500' : 'bg-red-500'}`} />
                <span className={`font-semibold text-gray-800 dark:text-gray-100 ${tvMode ? 'text-lg' : 'text-sm font-medium'}`}>{r.label}</span>
              </div>
              {/* En mode TV, seul le nom du moyen est utile : pas de « max Tc ». */}
              {!tvMode && <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-4">max {r.maxTc} Tc</span>}
            </div>

            <div
              className="relative flex-grow"
              onClick={(e) => {
                if (!onEmptyClick || trackWidth === 0) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const px = e.clientX - rect.left;
                const minutes = px / scale.pxPerMinute;
                onEmptyClick(r.id, new Date(from.getTime() + minutes * 60_000));
              }}
            >
              {/* Bandes jour + reperes verticaux */}
              {bands.map((band, i) => (
                <div key={i} className="absolute top-0 bottom-0 bg-amber-50/40 dark:bg-amber-400/5" style={{ left: band.x, width: band.width }} />
              ))}
              {ticks.map((t, i) => (
                <div key={i} className={`absolute top-0 bottom-0 border-l ${t.major ? 'border-gray-300 dark:border-gray-700' : 'border-gray-100 dark:border-gray-800'}`} style={{ left: t.x }} />
              ))}
              {nowX != null && <div className="absolute top-0 bottom-0 border-l-2 border-red-500 z-20" style={{ left: nowX }} />}

              {unavailable && (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 bg-[repeating-linear-gradient(45deg,transparent,transparent_8px,rgba(0,0,0,0.03)_8px,rgba(0,0,0,0.03)_16px)]">
                  {r.status === 'MAINTENANCE' ? 'Maintenance' : 'Hors service'}
                </div>
              )}

              {trackWidth > 0 &&
                (batchesByResource.get(r.id) ?? []).map((b) => {
                  const props = {
                    batch: b,
                    geometry: batchGeometry(scale, b),
                    rowHeight,
                    color: cycleColor(b.cycleCode, b.cycleColor),
                    selected: selectedBatchId === b.id,
                    feasibility: feasibilityByBatch?.[b.id],
                    badge: badges?.[b.id],
                    tvMode,
                    onClick: onBatchClick,
                  };
                  return droppableBatches ? <DroppableBatchBlock key={b.id} {...props} /> : <BatchBlock key={b.id} {...props} />;
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface BatchBlockProps {
  batch: PlanningBatch;
  geometry: ReturnType<typeof batchGeometry>;
  rowHeight: number;
  color: string;
  selected: boolean;
  feasibility?: BatchFeasibilityHint;
  badge?: string;
  tvMode?: boolean;
  isOver?: boolean;
  dropRef?: (el: HTMLElement | null) => void;
  onClick?: (b: PlanningBatch) => void;
}

/** Variante droppable : nécessite un DndContext parent (vue fusionnée). */
function DroppableBatchBlock(props: BatchBlockProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `batch-${props.batch.id}` });
  return <BatchBlock {...props} dropRef={setNodeRef} isOver={isOver} />;
}

/** Cadre/opacité selon la faisabilité (quand un OF est sélectionné). */
function feasibilityClass(f: BatchFeasibilityHint | undefined, isOver: boolean): string {
  if (isOver) return 'ring-2 ring-blue-500 z-20';
  if (!f) return '';
  switch (f.level) {
    case 'incompatible':
      return 'opacity-30 grayscale';
    case 'blocking':
      return 'ring-2 ring-red-500';
    case 'warning':
      return 'ring-2 ring-amber-400';
    case 'ok':
      return 'ring-2 ring-green-500';
  }
}

function BatchBlock({ batch, geometry, rowHeight, color, selected, feasibility, badge, tvMode = false, isOver = false, dropRef, onClick }: BatchBlockProps) {
  const running = batch.status === 'RUNNING' || batch.status === 'LOADING' || batch.status === 'UNLOADING';
  const feasCls = feasibilityClass(feasibility, isOver);
  // Une fournee affichee en carte (badge) est nettement soulignee : lien clair
  // entre le bloc de la timeline et son detail affiche en dessous.
  const badgeCls = badge ? 'ring-2 ring-blue-600 z-10 shadow-lg' : '';
  return (
    <div
      ref={dropRef}
      className={`absolute rounded overflow-hidden cursor-pointer transition-all ${badgeCls || feasCls || (selected ? 'ring-2 ring-blue-600 z-10' : 'hover:shadow-md')}`}
      style={{ left: geometry.left, width: Math.max(geometry.width, 2), top: 6, height: rowHeight - 12 }}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(batch);
      }}
      title={`${batch.number} · ${batch.cycleLabel}`}
    >
      {/* Badge de lien vers la carte de detail (mode TV) */}
      {badge && (
        <div className="absolute -top-1 -left-1 z-20 bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold shadow">
          {badge}
        </div>
      )}
      {/* Sous-blocs proportionnels : chargement | cycle | incertitude | dechargement */}
      <div className="flex h-full w-full">
        <div style={{ width: geometry.loadWidth, backgroundColor: color, opacity: 0.55 }} />
        <div className="relative flex-grow" style={{ backgroundColor: color, opacity: 0.85, width: geometry.cycleWidth }} />
        {geometry.hasUncertainty && (
          <div
            style={{
              width: geometry.uncertaintyWidth,
              backgroundImage: `repeating-linear-gradient(45deg, ${color}, ${color} 4px, transparent 4px, transparent 8px)`,
              opacity: 0.5,
            }}
            title="Zone d'incertitude (durée mini → maxi)"
          />
        )}
        <div style={{ width: geometry.unloadWidth, backgroundColor: color, opacity: 0.55 }} />
      </div>

      {/* Etiquette par-dessus */}
      <div className={`absolute inset-0 flex flex-col justify-center pointer-events-none ${badge ? 'pl-4 pr-1.5' : 'px-1.5'} py-0.5`}>
        <div className={`flex items-center gap-1 text-white font-semibold drop-shadow truncate ${tvMode ? 'text-base' : 'text-xs'}`}>
          {batch.pinned && <span title="Épinglée">📌</span>}
          <span className="opacity-90 font-bold">{batch.cycleCode}</span>
          <span className="font-normal opacity-90">· {batch.number}</span>
        </div>
        {geometry.width > 60 && (
          <div className={`text-white/90 truncate drop-shadow ${tvMode ? 'text-xs' : 'text-[10px]'}`}>
            {batch.itemCount} OF{running ? ' · en cours' : ''}
          </div>
        )}
      </div>

      {/* Badges de faisabilité (Tc/voies/délai) quand un OF est sélectionné */}
      {feasibility && feasibility.icons.length > 0 && feasibility.level !== 'incompatible' && (
        <div className="absolute top-0.5 right-0.5 flex gap-0.5 bg-white/85 rounded px-1 text-[11px] leading-none py-0.5">
          {feasibility.icons.map((ic, i) => (
            <span key={i}>{ic}</span>
          ))}
        </div>
      )}
    </div>
  );
}
